import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * React Bits AnimatedContent — scroll-triggered entrance (default) or mount trigger.
 * @see https://www.reactbits.dev/animations/animated-content
 */
export default function AnimatedContent({
  children,
  container,
  distance = 100,
  direction = 'vertical',
  reverse = false,
  duration = 0.8,
  ease = 'power3.out',
  exitEase = 'power3.in',
  initialOpacity = 0,
  animateOpacity = true,
  scale = 1,
  threshold = 0.1,
  delay = 0,
  disappearAfter = 0,
  disappearDuration = 0.5,
  disappearEase = 'power3.in',
  triggerOnMount = false,
  isExiting = false,
  onExitComplete,
  onComplete,
  onDisappearanceComplete,
  className = '',
  ...props
}) {
  const ref = useRef(null);
  const enterTweenRef = useRef(null);
  const exitTweenRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  const onExitCompleteRef = useRef(onExitComplete);
  const onDisappearanceCompleteRef = useRef(onDisappearanceComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    onExitCompleteRef.current = onExitComplete;
  }, [onExitComplete]);

  useEffect(() => {
    onDisappearanceCompleteRef.current = onDisappearanceComplete;
  }, [onDisappearanceComplete]);

  const axis = direction === 'horizontal' ? 'x' : 'y';
  const offset = reverse ? -distance : distance;

  useEffect(() => {
    if (isExiting) return undefined;

    const el = ref.current;
    if (!el) return undefined;

    let scrollerTarget = container || document.getElementById('snap-main-container') || null;

    if (typeof scrollerTarget === 'string') {
      scrollerTarget = document.querySelector(scrollerTarget);
    }

    const startPct = (1 - threshold) * 100;

    gsap.set(el, {
      [axis]: offset,
      scale,
      opacity: animateOpacity ? initialOpacity : 1,
      visibility: 'visible',
    });

    const tl = gsap.timeline({
      paused: true,
      delay,
      onComplete: () => {
        onCompleteRef.current?.();
        if (disappearAfter > 0) {
          gsap.to(el, {
            [axis]: reverse ? distance : -distance,
            scale: 0.8,
            opacity: animateOpacity ? initialOpacity : 0,
            delay: disappearAfter,
            duration: disappearDuration,
            ease: disappearEase,
            onComplete: () => onDisappearanceCompleteRef.current?.(),
          });
        }
      },
    });

    tl.to(el, {
      [axis]: 0,
      scale: 1,
      opacity: 1,
      duration,
      ease,
    });

    enterTweenRef.current = tl;

    if (triggerOnMount) {
      tl.play();
      return () => {
        tl.kill();
        enterTweenRef.current = null;
      };
    }

    const st = ScrollTrigger.create({
      trigger: el,
      scroller: scrollerTarget,
      start: `top ${startPct}%`,
      once: true,
      onEnter: () => tl.play(),
    });

    return () => {
      st.kill();
      tl.kill();
      enterTweenRef.current = null;
    };
  }, [
    container,
    distance,
    direction,
    reverse,
    duration,
    ease,
    initialOpacity,
    animateOpacity,
    scale,
    threshold,
    delay,
    disappearAfter,
    disappearDuration,
    disappearEase,
    triggerOnMount,
    isExiting,
    axis,
    offset,
  ]);

  useEffect(() => {
    if (!isExiting) return undefined;

    const el = ref.current;
    if (!el) return undefined;

    enterTweenRef.current?.kill();
    enterTweenRef.current = null;
    exitTweenRef.current?.kill();

    exitTweenRef.current = gsap.to(el, {
      [axis]: offset,
      scale,
      opacity: animateOpacity ? initialOpacity : 1,
      duration,
      ease: exitEase,
      onComplete: () => {
        exitTweenRef.current = null;
        onExitCompleteRef.current?.();
      },
    });

    return () => {
      exitTweenRef.current?.kill();
      exitTweenRef.current = null;
    };
  }, [
    isExiting,
    axis,
    offset,
    scale,
    initialOpacity,
    animateOpacity,
    duration,
    exitEase,
    onExitComplete,
  ]);

  return (
    <div ref={ref} className={className} {...props}>
      {children}
    </div>
  );
}
